import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { useSearchParams } from 'react-router-dom';
import ClientNav from '@/components/ClientNav';
import { Button } from '@/components/ui/button';
import { CreditCard, Clock, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ClientInvoices = () => {
  const { token } = useAuth();
  const [searchParams] = useSearchParams();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState({});

  useEffect(() => {
    fetchInvoices();

    const sessionId = searchParams.get('session_id');
    const paymentStatus = searchParams.get('payment');

    if (sessionId && paymentStatus === 'success') {
      pollPaymentStatus(sessionId);
    } else if (paymentStatus === 'cancelled') {
      toast.error('Payment cancelled');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const fetchInvoices = async () => {
    try {
      const response = await axios.get(`${API}/client/invoices`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInvoices(response.data.invoices || []);
    } catch (error) {
      console.error('Failed to fetch invoices:', error);
    }
  };

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    if (attempts >= maxAttempts) {
      toast.error('Payment verification timed out');
      return;
    }

    try {
      const response = await axios.get(`${API}/payments/status/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.payment_status === 'paid') {
        toast.success('Payment successful!');
        fetchInvoices();
        return;
      } else if (response.data.status === 'expired') {
        toast.error('Payment session expired');
        return;
      }

      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    } catch (error) {
      console.error('Error checking payment status:', error);
      toast.error('Failed to verify payment');
    }
  };

  const getCurrencySymbol = (currency) => {
    const c = (currency || 'gbp').toLowerCase();
    if (c === 'gbp') return '£';
    if (c === 'usd') return '$';
    if (c === 'eur') return '€';
    return ''; // fallback: no symbol
  };

  const formatMoney = (amount, currency) => {
    const symbol = getCurrencySymbol(currency);
    const safeAmount = Number(amount || 0);
    return `${symbol}${safeAmount.toFixed(2)}`;
  };

  const parseDateSafe = (dateVal) => {
    // backend may store ISO string; new Date() handles it
    const d = new Date(dateVal);
    return isNaN(d.getTime()) ? null : d;
  };

  const handlePayInvoice = async (invoiceId) => {
    setLoading((prev) => ({ ...prev, [invoiceId]: true }));

    try {
      // ✅ IMPORTANT: backend route is /payments/create-checkout
      const response = await axios.post(
        `${API}/payments/create-checkout`,
        { invoice_id: invoiceId },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            Origin: window.location.origin
          }
        }
      );

      if (response.data.url) {
        window.location.href = response.data.url;
      } else {
        toast.error('Payment link not returned');
        setLoading((prev) => ({ ...prev, [invoiceId]: false }));
      }
    } catch (error) {
      console.error('Failed to create checkout:', error);
      toast.error('Failed to initiate payment');
      setLoading((prev) => ({ ...prev, [invoiceId]: false }));
    }
  };

  const getStatusIcon = (status) => {
    switch ((status || '').toLowerCase()) {
      case 'paid':
        return <CheckCircle2 className="text-green-400" size={20} />;
      default:
        return <Clock className="text-yellow-400" size={20} />;
    }
  };

  return (
    <div className="min-h-screen bg-[#030712]" data-testid="client-invoices-page">
      <ClientNav />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-4xl font-bold font-['Syne'] text-white mb-2" data-testid="invoices-title">
            Invoices
          </h1>
          <p className="text-slate-400">View and pay your invoices</p>
        </div>

        {invoices.length === 0 ? (
          <div className="bg-slate-950 border border-slate-800 rounded-xl p-12 text-center" data-testid="no-invoices">
            <CreditCard className="w-16 h-16 text-slate-700 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">No invoices yet</h3>
            <p className="text-slate-400">Your invoices will appear here</p>
          </div>
        ) : (
          <div className="space-y-4" data-testid="invoices-list">
            {invoices.map((invoice) => {
              const dueDate = parseDateSafe(invoice.due_date);

              return (
                <div
                  key={invoice.id}
                  className="bg-slate-950 border border-slate-800 rounded-xl p-6 flex items-center justify-between"
                  data-testid={`invoice-${invoice.id}`}
                >
                  <div className="flex items-center space-x-4">
                    {getStatusIcon(invoice.status)}
                    <div>
                      <h3 className="text-xl font-semibold text-white">
                        {formatMoney(invoice.amount, invoice.currency)}
                      </h3>
                      <p className="text-sm text-slate-400">
                        Due: {dueDate ? format(dueDate, 'MMMM dd, yyyy') : 'N/A'}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center space-x-4">
                    <span
                      className={`px-3 py-1 rounded-full text-xs font-medium ${
                        invoice.status === 'paid'
                          ? 'bg-green-500/10 text-green-400'
                          : 'bg-yellow-500/10 text-yellow-400'
                      }`}
                    >
                      {invoice.status}
                    </span>

                    {invoice.status !== 'paid' && (
                      <Button
                        onClick={() => handlePayInvoice(invoice.id)}
                        disabled={loading[invoice.id]}
                        className="bg-white text-black hover:bg-slate-200"
                        data-testid={`pay-invoice-${invoice.id}`}
                      >
                        {loading[invoice.id] ? 'Processing...' : 'Pay Now'}
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientInvoices;
